import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Stack,
    Alert,
    CircularProgress,
    Pagination,
    Card,
    IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { RECOGIDA_ESTADO, RECOGIDA_ESTADOS_CANCELABLES } from "../../constants";
import EstadoBadge from "../ui/EstadoBadge";
import { formatearDireccion } from "../../helpers/direccion";
import { formatearFechaHora } from "../../helpers/fechas";

/**
 * Vista mobile con lista paginada de recogidas
 */
export default function RecogidasMobileList({ rows, loading, onCancelar, onRecepcionar, onEliminar, onVerSeguimiento }) {
    const [page, setPage] = useState(1);
    const pageSize = 10;

    // Si cambian los rows (filtros), vuelve a la primera página
    useEffect(() => {
        setPage(1);
    }, [rows]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: "#cc071e" }} />
            </Box>
        );
    }

    if (!rows?.length) {
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                No hay recogidas para mostrar.
            </Alert>
        );
    }

    const totalPages = Math.ceil(rows.length / pageSize);
    const start = (page - 1) * pageSize;
    const pageRows = rows.slice(start, start + pageSize);

    // Color del acento (borde izquierdo) alineado a la paleta corporativa unificada
    const estadoBorderColors = {
        [RECOGIDA_ESTADO.SOLICITADA]: '#f39c12',
        [RECOGIDA_ESTADO.CONFIRMADA]: '#27ae60',
        [RECOGIDA_ESTADO.ERROR]: '#e74c3c',
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Stack spacing={2}>
                {pageRows.map((recogida) => {
                    const estado = (recogida.estado ?? "").toUpperCase();
                    const borderColor = estadoBorderColors[estado] || '#95a5a6';

                    // Solo se puede eliminar en SOLICITADA o ERROR
                    const puedeEliminar = estado === RECOGIDA_ESTADO.SOLICITADA || estado === RECOGIDA_ESTADO.ERROR;
                    const puedeCancelar = RECOGIDA_ESTADOS_CANCELABLES.includes(estado);
                    const puedeRecepcionar = estado === RECOGIDA_ESTADO.REALIZADA;

                    return (
                        <Card
                            key={recogida.id}
                            sx={{
                                borderLeft: `4px solid ${borderColor}`,
                                p: 2,
                                boxShadow: 1,
                            }}
                        >
                            <Stack spacing={1.5}>
                                {/* Cabecera: Ticket y Estado */}
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <ConfirmationNumberIcon fontSize="small" color="action" />
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            {recogida.numeroTicket}
                                        </Typography>
                                    </Stack>
                                    <EstadoBadge estado={recogida.estado} />
                                </Stack>

                                {/* Cliente */}
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <PersonIcon fontSize="small" color="action" />
                                    <Typography variant="body2" color="text.secondary">
                                        {recogida.clienteNombre}
                                    </Typography>
                                </Stack>

                                {/* Dirección */}
                                <Stack direction="row" alignItems="flex-start" spacing={1}>
                                    <LocationOnIcon fontSize="small" color="action" sx={{ mt: 0.2 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {formatearDireccion(recogida.direccion)}
                                    </Typography>
                                </Stack>

                                {/* Situación en MSoft: abre el seguimiento al pulsar */}
                                {onVerSeguimiento && (
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <LocalShippingIcon fontSize="small" color="action" />
                                        <Typography
                                            variant="body2"
                                            onClick={() => onVerSeguimiento(recogida)}
                                            sx={{
                                                color: recogida.seguimiento?.situacionMsoft ? "#495057" : "#6c757d",
                                                cursor: "pointer",
                                                textDecoration: "underline dotted",
                                            }}
                                        >
                                            {recogida.seguimiento?.situacionMsoft || 'Sin sincronizar'}
                                        </Typography>
                                    </Stack>
                                )}

                                {/* Observaciones (si existen) */}
                                {recogida.observaciones && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            fontStyle: 'italic',
                                            pl: 4,
                                            borderLeft: '2px solid',
                                            borderColor: 'grey.300',
                                        }}
                                    >
                                        {recogida.observaciones}
                                    </Typography>
                                )}

                                {/* Creación y usuario */}
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <CalendarTodayIcon fontSize="small" color="action" />
                                    <Typography variant="caption" color="text.secondary">
                                        {formatearFechaHora(recogida.creado)}
                                        {recogida.usuario?.username ? ` · ${recogida.usuario.username}` : ''}
                                    </Typography>
                                </Stack>

                                {/* Botones de acción */}
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    {puedeCancelar && (
                                        <IconButton
                                            size="small"
                                            title="Cancelar recogida"
                                            onClick={() => onCancelar(recogida)}
                                            sx={{ color: '#495057' }}
                                        >
                                            <BlockIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                    {puedeRecepcionar && (
                                        <IconButton
                                            size="small"
                                            title="Recibida en almacén"
                                            onClick={() => onRecepcionar(recogida)}
                                            sx={{ color: '#196f3d' }}
                                        >
                                            <WarehouseIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        size="small"
                                        onClick={() => onEliminar(recogida)}
                                        disabled={!puedeEliminar}
                                        sx={{
                                            color: puedeEliminar ? 'error.main' : 'grey.400',
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Card>
                    );
                })}
            </Stack>

            {/* Paginación */}
            {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(e, val) => setPage(val)}
                        color="primary"
                    />
                </Box>
            )}
        </Box>
    );
}
